import Loader from "components/DesignSystem/Loader/loader";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { Form, Formik, FormikHelpers, useFormikContext } from "formik";
import { AnimatePresence, motion } from "framer-motion";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import { useEffect, useMemo, useState } from "react";
import { RowSelectionState } from "react-table-8.10.7";
import {
  useBulkAssignMerchantMutation,
  useGetAllTransactionsDataQuery,
} from "store/apis/transactions";
import { BackendError } from "types/utils/error";
import { ModalProps } from "types/utils/modal";
import { object, string } from "yup";
import { TransactionsView } from "components/TransactionsView/TransactionsView";
import { Transactions } from "types/Models/books";
import {
  DisclaimerUI,
  MerchantComponent,
} from "components/Transaction/BulkAssignVendor";

type BulkVendorAssignProps = ModalProps & {
  deselectRows: () => void;
  transactions: RowSelectionState;
};

export const BulkVendorAssign = ({
  close,
  isOpen,
  deselectRows,
  transactions = {},
}: BulkVendorAssignProps) => {
  const entityId = useCurrentEntityId();
  const { uuid: groupId } = useCurrentGroupContext();
  const { alertToast, successToast } = useToast();
  const [bulkAssignVendor] = useBulkAssignMerchantMutation();

  const [rowSelection, setRowSelection] = useState<RowSelectionState>(
    Object.fromEntries(
      Object.entries(transactions).map(([key, value]) => {
        const parsedKey = JSON.parse(key);
        return [parsedKey.uuid, value];
      })
    )
  );

  const selectedTxnIds = Object.entries(rowSelection);

  const selectedTransactions = useMemo(() => {
    return Object.entries(transactions)
      .filter(([_, selected]) => selected)
      .map(([key]) => {
        return JSON.parse(key);
      });
  }, [transactions]);

  const tableData = selectedTransactions.map((transaction) => {
    const { merchant } = transaction;
    return {
      transaction: {
        ...transaction,
        merchant: merchant?.name || null,
        logo: merchant?.logo || null,
      },
    };
  }) as unknown as Transactions[];

  const onSubmit = async (
    {
      vendorName,
      vendorId,
    }: {
      vendorName: string;
      vendorId: string;
    },
    {
      resetForm,
    }: FormikHelpers<{
      vendorName: string;
      vendorId: string;
    }>
  ) => {
    try {
      await bulkAssignVendor({
        groupId,
        entityId,
        payload: {
          entity_merchant_uuid: vendorId,
          transaction_ids: selectedTxnIds.map(([key, _]) => key).join(", "),
        },
      }).unwrap();

      close();
      deselectRows();
      setRowSelection({});
      resetForm();
      successToast({
        title: "Vendor assigned successfully",
        message: `For ${selectedTxnIds.length} transactions`,
      });
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };
  const handleCancel = () => {
    close();
  };

  return (
    <>
      <Modal.Root open={isOpen} onOpenChange={close}>
        <Formik
          initialValues={{ vendorName: "", vendorId: "" }}
          onSubmit={onSubmit}
          validateOnChange
          validateOnMount
          validationSchema={object({
            vendorId: string().required("Please select a vendor"),
          })}
        >
          {({
            submitForm,
            isSubmitting,
            isValid,
            values: { vendorId, vendorName },
            resetForm,
          }) => {
            return (
              <Modal.Content
                useCustomOverlay
                size={Boolean(vendorId) ? "xl" : "large"}
              >
                <Modal.Header>
                  <Modal.Title>Assign Vendor</Modal.Title>
                  <Modal.Close />
                </Modal.Header>
                <Modal.Body>
                  <Form className="t-m-0 t-flex t-flex-col t-gap-4">
                    <>
                      <MerchantComponent />
                      {vendorId && (
                        <AnimatePresence>
                          <DisclaimerUI
                            txnCount={selectedTxnIds.length}
                            selectedVendor={vendorName || ""}
                          />

                          <motion.div
                            layout
                            initial={{ opacity: 0, y: 100 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                              ease: "easeInOut",
                              duration: 0.5,
                            }}
                          >
                            <TransactionsView
                              transactions={tableData}
                              rowSelection={rowSelection}
                              setRowSelection={setRowSelection}
                              showCheckbox
                            />
                          </motion.div>
                        </AnimatePresence>
                      )}
                    </>
                  </Form>
                </Modal.Body>
                <Modal.Footer>
                  <div className="t-flex t-justify-end t-gap-3">
                    <Button
                      type="reset"
                      onClick={handleCancel}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      customType="primary"
                      onClick={submitForm}
                      isLoading={isSubmitting}
                      disabled={
                        isSubmitting ||
                        !isValid ||
                        Boolean(selectedTxnIds.length === 0)
                      }
                    >
                      Assign
                    </Button>
                  </div>
                </Modal.Footer>
              </Modal.Content>
            );
          }}
        </Formik>
      </Modal.Root>
    </>
  );
};
